import React from 'react';
import { EyeIcon } from '@heroicons/react/24/outline';
import {
  findCandidateModuleScore,
  formatModuleScorePercentage,
  resolveModuleIndexForScreening,
} from './assessmentModuleColumns';
import { isPersonalityResult, describePersonality } from '../../components/results/personalityProfile';

const scoreButtonClass =
  'inline-flex items-center gap-1 rounded-md px-2 py-1 text-indigo-700 bg-indigo-50 hover:bg-indigo-100 focus:outline-none focus:ring-1 focus:ring-indigo-500 transition-colors';

export function AssessmentModuleColumnHeaders({ modules = [] }) {
  if (!modules.length) return null;
  return modules.map((mod) => (
    <th
      key={mod.moduleId}
      className="px-2 py-3 text-center text-xs font-medium text-white uppercase tracking-wider table-cell w-[88px] max-w-[88px]"
      title={mod.title}
    >
      <span className="block line-clamp-2 leading-snug mx-auto">{mod.title}</span>
    </th>
  ));
}

export function AssessmentModuleScoreCells({
  candidate,
  modules = [],
  screeningMeta,
  onViewModuleScore,
  isAssessmentDropped = false,
  isRawScoreClient = false,
}) {
  if (!modules.length) return null;

  return modules.map((mod) => {
    const moduleScore = findCandidateModuleScore(candidate, mod.moduleId);
    const score = moduleScore?.score;
    const maxScore = moduleScore?.maxScore;
    const percentage = formatModuleScorePercentage(score, maxScore);
    const moduleIndex = resolveModuleIndexForScreening(screeningMeta, mod.moduleId);
    const hasProfile = isPersonalityResult(moduleScore?.personalityResult);
    const isClickable =
      typeof onViewModuleScore === 'function' &&
      ((!isAssessmentDropped && candidate?.status !== 'In Progress' && score != null) || hasProfile);

    let displayVal = null;
    let title = null;
    if (isPersonalityResult(moduleScore?.personalityResult)) {
      // Personality module: no score; the column is too narrow for the trait chip, so it opens the profile.
      displayVal = 'Profile';
      title = `${mod.title} — ${describePersonality(moduleScore.personalityResult)}`;
    } else if (isRawScoreClient && score != null) {
      displayVal = maxScore ? `${score}/${maxScore}` : `${score}`;
    } else if (percentage != null) {
      displayVal = `${percentage}%`;
    }

    return (
      <td
        key={mod.moduleId}
        className="px-2 py-3 whitespace-nowrap text-sm text-center table-cell w-[88px] max-w-[88px]"
      >
        {(candidate?.status === 'In Progress' || isAssessmentDropped) && !title ? (
          <span className="text-gray-400">—</span>
        ) : displayVal == null ? (
          <span className="text-gray-400">—</span>
        ) : isClickable ? (
          <button
            type="button"
            onClick={() => onViewModuleScore(candidate, { moduleIndex })}
            className={scoreButtonClass}
            title={title || `View ${mod.title} — ${displayVal}`}
          >
            {displayVal}
            <EyeIcon className="h-3.5 w-3.5 shrink-0 opacity-80" />
          </button>
        ) : (
          <span className="text-sm font-medium text-gray-700">{displayVal}</span>
        )}
      </td>
    );
  });
}
